import { useAuth } from '~/composables/useAuth';
import { useAuthStore } from '~/stores/auth';
import { useToastStore } from '~/stores/toast';
import type { ApiErrorResponse, ApiErrorWithStatus } from '~/types/api';
import { createApiUrl } from '~/utils/api';

// 取得目前語系的函數
// 直接從 document.cookie 讀取 i18n_redirected cookie（由 Nuxt i18n 自動管理）
// 這樣可以確保在普通函數中也能正常運作，不需要 setup context
function getCurrentLocale(): string {
  try {
    if (import.meta.client && typeof document !== 'undefined') {
      try {
        // 從 document.cookie 讀取 i18n_redirected cookie
        const cookies = document.cookie.split(';');
        const i18nCookie = cookies.find((cookie) => cookie.trim().startsWith('i18n_redirected='));

        if (i18nCookie) {
          const locale = i18nCookie.split('=')[1]?.trim();
          // 驗證語系值是否有效
          if (locale && (locale === 'en' || locale === 'zh-TW')) {
            return locale;
          }
        }
      } catch {
        // 如果讀取 cookie 失敗，fallback 到預設語系
      }
    }

    // 使用預設語系（根據 nuxt.config.ts 的 defaultLocale）
    return 'en';
  } catch (error) {
    // 如果發生錯誤，使用預設語系
    return 'en';
  }
}

// 防止並發刷新 token 的 Promise
let refreshTokenPromise: Promise<boolean> | null = null;
let refreshCsrfTokenPromise: Promise<boolean> | null = null;

export interface ApiFetchOptions extends RequestInit {
  handleError?: boolean;
  requireAuth?: boolean;
  customHeaders?: Record<string, string>;
  // 內部標記，用於防止重試循環
  _isRetry?: boolean;
  // 內部標記，表示已經嘗試過刷新 token
  _refreshAttempted?: boolean;
  // 內部標記，表示已經嘗試過刷新 CSRF token
  _csrfAttempted?: boolean;
}

export async function useApiFetch(path: string, options: ApiFetchOptions) {
  const url = createApiUrl(path);
  const headers = new Headers(options.headers);
  const requestMethod = (options.method || 'GET').toUpperCase();

  // TODO: remove this
  // headers.set('Preview', 'true'); // for preview mode
  // 預設以 JSON 送出（避免 fallback 成 text/plain）
  const isFormData = options.body instanceof FormData;
  if (!headers.has('Content-Type') && options.body && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }

  // 根據目前語系設定 Accept-Language header
  const currentLocale = getCurrentLocale();
  headers.set('Accept-Language', currentLocale);

  // 設定時區 header
  if (import.meta.client && typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      headers.set('X-Timezone', timeZone);
    } catch (error) {
      // 如果無法取得時區，不設定 header（避免影響請求）
      console.warn('無法取得時區資訊:', error);
    }
  }

  // 處理認證
  // 使用 authStore.getToken() 而不是直接讀取 cookie，確保邏輯一致性
  // authStore 會根據當前環境正確讀取 cookie（localhost 或 .momentx.ai）
  let authStore: ReturnType<typeof useAuthStore> | null = null;
  if (options.requireAuth) {
    authStore = useAuthStore();
    const token = authStore.getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // 處理自定義 headers
  // EX : 'ORGUUID: 41eb17eb-f4db-42a5-9f72-6ed6793d5c79'
  if (options.customHeaders) {
    for (const [key, value] of Object.entries(options.customHeaders)) {
      headers.set(key, value);
    }
  }

  // POST/PUT/DELETE/PATCH 請求需要帶 CSRF token
  if (options.requireAuth && shouldSendCsrfToken(requestMethod) && authStore) {
    const csrfToken = authStore.getCsrfToken();
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
    // 確保 signal 被正確傳遞
    signal: options.signal,
  });

  if (!response.ok) {
    let error: ApiErrorResponse = {};
    try {
      error = await response.json();
    } catch {
      error = {};
    }

    // 處理 401 錯誤：嘗試刷新 token 並重試請求
    if (response.status === 401 && options.requireAuth && !options._isRetry) {
      // 嘗試刷新 token
      const refreshSuccess = await tryRefreshToken();

      if (refreshSuccess) {
        // 刷新成功，使用新的 token 重試請求
        return useApiFetch(path, {
          ...options,
          _isRetry: true, // 標記為重試，避免無限循環
        });
      }

      // 刷新失敗，處理錯誤並跳轉到登入頁
      // 標記已經嘗試過刷新，避免在 handleApiError 中重複刷新
      await handleApiError(response.status, error, { ...options, _refreshAttempted: true });

      if (options.handleError === false) {
        const errorWithStatus: ApiErrorWithStatus = {
          ...error,
          status: response.status,
          statusCode: response.status,
        };
        throw errorWithStatus;
      }
      const apiError = new Error(error.message || 'API request failed') as Error & {
        status?: number;
        statusCode?: number;
        code?: string;
      };
      apiError.status = response.status;
      apiError.statusCode = response.status;
      if (error.code) {
        apiError.code = error.code;
      }
      throw apiError;
    }

    // 處理 CSRF 403 錯誤：刷新 token 並重試
    if (
      response.status === 403 &&
      options.requireAuth &&
      shouldSendCsrfToken(requestMethod) &&
      !options._csrfAttempted
    ) {
      const csrfError = getCsrfErrorCode(error);
      if (csrfError === 'csrf_token_missing' || csrfError === 'csrf_token_invalid') {
        const refreshSuccess = await tryRefreshCsrfToken();
        if (refreshSuccess) {
          return useApiFetch(path, {
            ...options,
            _csrfAttempted: true,
          });
        }
      }
    }

    // 統一錯誤處理邏輯
    await handleApiError(response.status, error, options);

    if (options.handleError === false) {
      // 為錯誤物件添加狀態碼，方便 Sentry 過濾
      const errorWithStatus: ApiErrorWithStatus = {
        ...error,
        status: response.status,
        statusCode: response.status,
      };
      throw errorWithStatus;
    }
    // 為錯誤物件添加狀態碼，方便 Sentry 過濾
    const apiError = new Error(error.message || 'API request failed') as Error & {
      status?: number;
      statusCode?: number;
      code?: string;
    };
    apiError.status = response.status;
    apiError.statusCode = response.status;
    if (error.code) {
      apiError.code = error.code;
    }
    throw apiError;
  }

  return response;
}

// 嘗試刷新 token 的函數（防止並發）
async function tryRefreshToken(): Promise<boolean> {
  // 如果已經有正在進行的刷新請求，等待它完成
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  // 創建新的刷新請求
  refreshTokenPromise = (async () => {
    try {
      const auth = useAuth();
      const authStore = useAuthStore();

      // 檢查是否有 refresh token
      if (!authStore.getRefreshToken()) {
        return false;
      }

      // 嘗試刷新 token（不觸發登入流程的重定向）
      const success = await auth.refreshTokenSilently();
      return success;
    } catch (error) {
      console.warn('Token refresh failed:', error);
      return false;
    } finally {
      // 清除 Promise，允許下次刷新
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
}

// 嘗試刷新 CSRF token（防止並發）
async function tryRefreshCsrfToken(): Promise<boolean> {
  if (refreshCsrfTokenPromise) {
    return refreshCsrfTokenPromise;
  }

  refreshCsrfTokenPromise = (async () => {
    try {
      const auth = useAuth();
      return await auth.refreshCsrfTokenSilently();
    } catch (error) {
      console.warn('CSRF token refresh failed:', error);
      return false;
    } finally {
      refreshCsrfTokenPromise = null;
    }
  })();

  return refreshCsrfTokenPromise;
}
function shouldSendCsrfToken(method: string): boolean {
  return method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH';
}

function getCsrfErrorCode(error: ApiErrorResponse): string | undefined {
  if (typeof error.error === 'string') {
    return error.error;
  }
  if (typeof error.code === 'string') {
    return error.code;
  }
  return undefined;
}

// 統一錯誤處理函數
async function handleApiError(status: number, error: ApiErrorResponse, options: ApiFetchOptions) {
  const toast = useToastStore();
  const auth = useAuth();
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;
  switch (status) {
    case 401:
      // 統一處理認證錯誤
      toast.error(t('errorMessages.api.unauthorized'));

      // 只有在已經嘗試過刷新但失敗的情況下，才執行登出和跳轉
      // 如果 _refreshAttempted 為 true，表示已經在 useApiFetch 中嘗試過刷新但失敗
      // 此時應該直接登出，不應該再調用 refreshTokenOnUnauthorized（避免重複刷新造成循環）
      if (options._refreshAttempted) {
        try {
          // 直接登出，不再次嘗試刷新 token
          // 跳過 API 調用，因為 token 已失效，調用 logoutApi 會導致 401 無限循環
          await auth.logout(undefined, true);
          // 跳轉到登入頁
          const localePath = useLocalePath();
          if (import.meta.client) {
            window.location.href = localePath('/login');
          }
        } catch (logoutError) {
          console.warn('Logout failed during error handling:', logoutError);
        }
      } else if (options.requireAuth && !options._isRetry) {
        // 如果這是 requireAuth 的請求但沒有嘗試過刷新（理論上不應該發生）
        // 這種情況應該在 useApiFetch 中已經處理，但為了安全起見，這裡也處理
        try {
          await auth.refreshTokenOnUnauthorized();
        } catch (refreshError) {
          console.warn('Token refresh failed, redirecting to login');
        }
      }
      break;
    case 403:
      toast.error(t('errorMessages.api.forbidden'));
      break;
    case 404:
      toast.error(t('errorMessages.api.notFound'));
      break;
    case 500:
      toast.error(t('errorMessages.api.serverError'));
      break;
    case 502:
    case 503:
    case 504:
      toast.error(t('errorMessages.api.serverError'));
      break;
    default:
      // 其他錯誤的統一處理
      if (error.message) {
        // toast.error(error.message);
      } else {
        // toast.error(t('errorMessages.api.unknown'));
      }
      break;
  }
}
